import React from "react";
import { AlertTriangle, CalendarClock, Copy, TrendingUp, UserCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { money, baseMoney } from "@/lib/apiClient";
import { ConfidenceBadge, confidenceTier } from "@/components/ConfidenceBadge";
import { DuplicateDecision } from "@/components/DuplicateDecision";
import { DocPeek } from "@/components/approve/DocPeek";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const dueText = (d) => {
  if (!d) return null;
  const days = Math.ceil((new Date(d) - new Date()) / 86400000);
  return days < 0 ? { t: `${-days}d overdue`, late: true } : days === 0 ? { t: "Due today", late: false } : { t: `Due in ${days}d`, late: false };
};

const LineRow = ({ li, cats, onCode, disabled }) => (
  <li className="rounded-lg border border-border p-3" data-testid={`approve-line-${li.id}`}>
    <div className="flex items-start justify-between gap-3">
      <span className="min-w-0 flex-1 text-sm leading-snug">{li.description || "Line item"}</span>
      <span className="shrink-0 font-mono text-sm font-semibold">{money(li.amount)}</span>
    </div>
    <div className="mt-2 flex items-center gap-2">
      <span className={cn("h-2 w-2 shrink-0 rounded-full", li.confidence >= 0.8 ? "bg-emerald-500" : li.confidence >= 0.6 ? "bg-amber-500" : "bg-red-500")} title={confidenceTier(li.confidence).label} />
      <select value={li.gl_code || ""} disabled={disabled} onChange={(e) => onCode(li.id, e.target.value)} data-testid={`approve-gl-${li.id}`}
              className="h-10 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm">
        {!cats.some((c) => c.code === li.gl_code) && <option value={li.gl_code || ""}>{li.gl_code ? `${li.gl_code} · ${li.gl_name || "unknown"}` : "— pick a code —"}</option>}
        {cats.map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
      </select>
      <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{Math.round((li.confidence || 0) * 100)}%</span>
    </div>
  </li>
);

export const ApproveCard = ({ inv, cats, user, onCode, onDuplicateResolved, busy }) => {
  const due = dueText(inv.due_date);
  const fx = inv.fx_alert && !inv.fx_alert.resolved ? inv.fx_alert : null;
  return (
    <article className="space-y-4" data-testid={`approve-card-${inv.id}`}>
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-heading truncate text-xl font-semibold" data-testid="approve-supplier">{inv.supplier || "Unknown supplier"}</h2>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span className="font-mono" data-testid="approve-invoice-number">{inv.invoice_number || "no number"}</span>
            <span>· {fmtDate(inv.invoice_date)}</span>
            {inv.assigned_to && <span className="inline-flex items-center gap-1 text-primary" data-testid="approve-assigned"><UserCheck className="h-3 w-3" /> {inv.assigned_to === user ? "assigned to you" : inv.assigned_to}</span>}
          </div>
        </div>
        <ConfidenceBadge value={inv.min_confidence} showLabel={false} className="shrink-0" />
      </header>

      <div className="flex items-end justify-between gap-3 rounded-xl bg-primary/5 px-4 py-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Total</div>
          <div className="font-mono text-3xl font-bold tracking-tight" data-testid="approve-total">{money(inv.total, inv.currency)}</div>
          {baseMoney(inv) && <div className="font-mono text-xs text-muted-foreground" data-testid="approve-base-total">≈ {baseMoney(inv)} · rate {inv.fx_rate}</div>}
        </div>
        {due && <span className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium", due.late ? "bg-red-500/15 text-red-500" : "bg-muted text-muted-foreground")} data-testid="approve-due"><CalendarClock className="h-3.5 w-3.5" /> {due.t}</span>}
      </div>

      <DocPeek inv={inv} />

      {inv.possible_duplicate && !inv.duplicate_resolved && <DuplicateDecision inv={inv} user={user} onResolved={onDuplicateResolved} />}

      {fx && (
        <div className="flex gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs" data-testid="approve-fx-alert">
          <TrendingUp className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
          <span>Exchange rate {fx.rate} is {Math.abs(fx.change_pct).toFixed(1)}% {fx.change_pct > 0 ? "above" : "below"} this supplier's usual {fx.usual_rate}. Fix the rate from the desktop invoice page if needed.</span>
        </div>
      )}

      {inv.review_reasons?.length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2.5" data-testid="approve-reasons">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400"><AlertTriangle className="h-3.5 w-3.5" /> Why this needs you</div>
          <ul className="mt-1 space-y-0.5 text-sm">{inv.review_reasons.map((r) => <li key={r} className="flex gap-2"><span className="text-amber-500">•</span><span>{r}</span></li>)}</ul>
        </div>
      )}

      <section>
        <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
          <span className="font-semibold uppercase tracking-wide">Coding · {inv.line_items.length} line{inv.line_items.length === 1 ? "" : "s"}</span>
          <span>net {money(inv.subtotal, inv.currency)} · tax {money(inv.tax, inv.currency)}</span>
        </div>
        <ul className="space-y-2">{inv.line_items.map((li) => <LineRow key={li.id} li={li} cats={cats} onCode={onCode} disabled={busy} />)}</ul>
      </section>

      {inv.possible_duplicate && inv.duplicate_resolved?.decision === "keep" && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Copy className="h-3.5 w-3.5" /> Duplicate check cleared by {inv.duplicate_resolved.by}.</p>
      )}
    </article>
  );
};
